import { blogPosts } from "../data/blogPosts";

const DAY_MS = 24 * 60 * 60 * 1000;

const stripHtml = (value = "") =>
  String(value)
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const tokenize = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);

const overlapCount = (a = [], b = []) => {
  const setB = new Set(b);
  let count = 0;
  const seen = new Set();

  a.forEach((item) => {
    if (setB.has(item) && !seen.has(item)) {
      seen.add(item);
      count += 1;
    }
  });

  return count;
};

const toEpoch = (value) => {
  const date = new Date(value);
  const ts = date.getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const calculateRecencyBoost = (post) => {
  const referenceDate = post.updatedISO || post.dateISO;
  const ageMs = Date.now() - toEpoch(referenceDate);
  const ageDays = Math.max(0, ageMs / DAY_MS);
  const normalized = Math.max(0, 1 - Math.min(ageDays, 365) / 365);
  return Number((normalized * 2).toFixed(3));
};

const calculateTitleSimilarity = (titleA, titleB) => {
  const tokensA = tokenize(titleA);
  const tokensB = tokenize(titleB);
  if (!tokensA.length || !tokensB.length) return 0;
  const shared = overlapCount(tokensA, tokensB);
  const union = new Set([...tokensA, ...tokensB]).size;
  if (!union) return 0;
  const score = Math.round((shared / union) * 5);
  return Math.max(0, Math.min(5, score));
};

const compareByDateDesc = (left, right) => {
  const rightTs = toEpoch(right.updatedISO || right.dateISO);
  const leftTs = toEpoch(left.updatedISO || left.dateISO);
  if (rightTs !== leftTs) return rightTs - leftTs;
  return String(left.slug).localeCompare(String(right.slug));
};

export const estimateReadingMinutes = (content = "") => {
  const plain = stripHtml(content);
  const words = plain ? plain.split(/\s+/).filter(Boolean).length : 0;
  const estimated = Math.ceil(words / 200);
  return Math.max(1, estimated || 1);
};

export const getPostsByCategory = (categorySlug, posts = blogPosts) =>
  [...posts]
    .filter((post) => post.categorySlug === categorySlug)
    .sort(compareByDateDesc);

export const getPostsByTag = (tag, posts = blogPosts) => {
  const normalized = String(tag || "").toLowerCase();
  if (!normalized) return [];
  return [...posts]
    .filter((post) =>
      Array.isArray(post.tags)
        ? post.tags.some((item) => String(item).toLowerCase() === normalized)
        : false
    )
    .sort(compareByDateDesc);
};

export const getLatestPosts = (posts = blogPosts, limit = 6) =>
  [...posts].sort(compareByDateDesc).slice(0, Math.max(1, limit));

export const getRelatedPosts = (currentPost, allPosts = blogPosts, limit = 6) => {
  if (!currentPost?.id) return [];
  const currentTags = (currentPost.tags || []).map((tag) => String(tag).toLowerCase());
  const currentKeywords = (currentPost.keywords || []).map((kw) => String(kw).toLowerCase());

  return allPosts
    .filter((post) => post.id !== currentPost.id)
    .map((post, index) => {
      const candidateTags = (post.tags || []).map((tag) => String(tag).toLowerCase());
      const candidateKeywords = (post.keywords || []).map((kw) => String(kw).toLowerCase());

      const sameCategoryScore = post.categorySlug === currentPost.categorySlug ? 6 : 0;
      const sharedTagScore = Math.min(overlapCount(currentTags, candidateTags), 3) * 3;
      const sharedKeywordScore = Math.min(overlapCount(currentKeywords, candidateKeywords), 5) * 2;
      const titleSimilarityScore = calculateTitleSimilarity(currentPost.title, post.title);
      const recencyBoost = calculateRecencyBoost(post);

      const totalScore =
        sameCategoryScore +
        sharedTagScore +
        sharedKeywordScore +
        titleSimilarityScore +
        recencyBoost;

      return {
        post,
        index,
        totalScore,
      };
    })
    .sort((left, right) => {
      if (right.totalScore !== left.totalScore) return right.totalScore - left.totalScore;

      const rightTs = toEpoch(right.post.updatedISO || right.post.dateISO);
      const leftTs = toEpoch(left.post.updatedISO || left.post.dateISO);
      if (rightTs !== leftTs) return rightTs - leftTs;

      const slugCompare = String(left.post.slug).localeCompare(String(right.post.slug));
      if (slugCompare !== 0) return slugCompare;

      return left.index - right.index;
    })
    .slice(0, Math.max(1, limit))
    .map((item) => item.post);
};

export const getRelatedCategories = (
  currentPost,
  categories,
  posts = blogPosts,
  limit = 3
) => {
  if (!currentPost || !Array.isArray(categories)) return [];

  const currentTags = (currentPost.tags || []).map((tag) => String(tag).toLowerCase());
  const currentKeywords = (currentPost.keywords || []).map((kw) => String(kw).toLowerCase());

  return categories
    .filter((category) => category.slug !== currentPost.categorySlug)
    .map((category, index) => {
      const categoryPosts = posts.filter((post) => post.categorySlug === category.slug);
      const categoryTags = categoryPosts.flatMap((post) =>
        (post.tags || []).map((tag) => String(tag).toLowerCase())
      );
      const categoryKeywords = categoryPosts.flatMap((post) =>
        (post.keywords || []).map((kw) => String(kw).toLowerCase())
      );

      const tagScore = Math.min(overlapCount(currentTags, categoryTags), 3) * 2;
      const keywordScore = Math.min(overlapCount(currentKeywords, categoryKeywords), 5);
      const volumeScore = Math.min(categoryPosts.length, 4);

      return {
        category,
        index,
        score: tagScore + keywordScore + volumeScore,
      };
    })
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      const nameCompare = String(left.category.name).localeCompare(String(right.category.name));
      if (nameCompare !== 0) return nameCompare;
      return left.index - right.index;
    })
    .slice(0, Math.max(1, limit))
    .map((item) => item.category);
};

export const getPopularTags = (posts = blogPosts, limit = 12) => {
  const frequency = new Map();

  posts.forEach((post) => {
    (post.tags || []).forEach((tag) => {
      const key = String(tag || "").trim();
      if (!key) return;
      const normalized = key.toLowerCase();
      frequency.set(normalized, {
        label: key,
        count: (frequency.get(normalized)?.count || 0) + 1,
      });
    });
  });

  return [...frequency.entries()]
    .sort((left, right) => {
      if (right[1].count !== left[1].count) return right[1].count - left[1].count;
      return left[1].label.localeCompare(right[1].label);
    })
    .slice(0, Math.max(1, limit))
    .map((entry) => entry[1].label);
};
